import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { toast } from "sonner";
import { Upload, Sparkles, RefreshCw, Trash2, Plus, X } from "lucide-react";

const field = "w-full bg-race-bg border border-race-line rounded-md px-3 py-2.5 text-sm text-race-txt outline-none focus:border-race-volt";
const src = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

export default function AdvCreativeStudio() {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", image_url: "", prompt: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/adv/creatives").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) return toast.error("Choose an image.");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/adv/creatives/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, image_url: data.url })); toast.success("Uploaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const generate = async () => {
    if (!draft.prompt.trim()) return toast.error("Describe the ad first.");
    setBusy(true);
    try { const { data } = await api.post("/adv/creatives/generate", { prompt: draft.prompt.trim(), format: "double" });
      setDraft((d) => ({ ...d, image_url: data.url })); toast.success("Generated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = async () => {
    if (!draft.image_url) return toast.error("Add or generate an image first.");
    try { await api.post("/adv/creatives", { name: draft.name || "Untitled ad", creatives: { double: draft.image_url }, image_url: draft.image_url });
      toast.success("Saved to library"); setOpen(false); setDraft({ name: "", image_url: "", prompt: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/adv/creatives/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <AdvShell>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div><h1 className="font-heading text-4xl tracking-tight" data-testid="adv-studio-title">Creative Studio</h1><p className="text-race-sub mt-1">Reusable ads you can drop into any campaign.</p></div>
        <button onClick={() => setOpen(true)} data-testid="adv-studio-new" className="inline-flex items-center gap-2 bg-race-volt text-race-bg font-semibold text-sm px-4 py-2.5 rounded-md hover:opacity-90"><Plus size={16} /> New Ad</button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-race-line py-16 text-center text-race-sub" data-testid="adv-studio-empty">No saved ads yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="adv-studio-grid">
          {rows.map((c) => (
            <div key={c.id} className="bg-race-surface border border-race-line rounded-lg overflow-hidden" data-testid={`adv-creative-${c.id}`}>
              <div className="bg-race-bg aspect-[324/132]">{c.image_url && <img src={src(c.image_url)} alt={c.name} className="w-full h-full object-cover" />}</div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-race-muted">Used in {c.usage} campaign(s)</div></div>
                <button onClick={() => del(c.id)} className="text-race-muted hover:text-race-red shrink-0" data-testid={`adv-creative-del-${c.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-race-surface border border-race-line rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="adv-studio-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-heading text-xl">New ad</h3><button onClick={() => setOpen(false)}><X size={18} className="text-race-sub" /></button></div>
            <input className={`${field} mb-3`} placeholder="Ad name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="adv-studio-name" />
            {draft.image_url ? (
              <div className="mb-3"><img src={src(draft.image_url)} alt="" className="w-full rounded border border-race-line" /><button onClick={() => setDraft({ ...draft, image_url: "" })} className="text-xs text-race-sub hover:text-race-red mt-2">Remove image</button></div>
            ) : (
              <div className="space-y-3 mb-3">
                <button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full border border-dashed border-race-line rounded-md py-4 text-sm text-race-sub hover:border-race-volt hover:text-race-volt inline-flex items-center justify-center gap-2"><Upload size={16} /> Upload image</button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                <div className="border border-race-volt/40 bg-race-volt/5 rounded-md p-3">
                  <textarea rows={2} className={`${field} resize-none`} placeholder="Describe an ad to generate with AI…" value={draft.prompt} onChange={(e) => setDraft({ ...draft, prompt: e.target.value })} data-testid="adv-studio-prompt" />
                  <button onClick={generate} disabled={busy} className="w-full mt-2 bg-race-volt text-race-bg font-semibold rounded-md py-2 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate with AI</button>
                </div>
              </div>
            )}
            <button onClick={save} data-testid="adv-studio-save" className="w-full bg-race-txt text-race-bg font-semibold rounded-md py-2.5 text-sm">Save to library</button>
          </div>
        </div>
      )}
    </AdvShell>
  );
}
